/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * DR plans (DR Phase 9): what a plan recovers and in which order, and whether it can run now. A plan's spec
 * is versioned by Swift; every change makes a new version, which is validated before the plan is READY.
 */

import * as React from "react";
import styled from "styled-components";
import {
  Archive,
  ClipboardCheck,
  FlaskConical,
  History,
  ListChecks,
  Pencil,
  PlaneLanding,
  PlaneTakeoff,
  RotateCcw,
  Siren,
} from "lucide-react";

import { ErrorNotice, Footer } from "@src/components/modules/DrModule/DrDialogs";
import { OPERATION_LABELS, RUN_STATES, RunView } from "@src/components/modules/DrModule/DrRuns";
import { drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, Pill, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import type { PillTone } from "@src/components/ui/Lists/ListTable";
import {
  CheckRow,
  FieldHint,
  FootSpacer,
  FormField,
  FormGrid,
  Notice,
  OpsDialog,
  OpsDrawer,
  OpsInput,
  OpsTextarea,
  SectionHeading,
} from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";

import type {
  DrCheckStatus,
  DrGroup,
  DrPlan,
  DrPlanCheck,
  DrPlanSpec,
  DrPlanVersion,
  DrRecoveryPoint,
  DrRun,
  DrWorkload,
} from "@src/@types/Dr";

export const PLAN_STATES: { [state: string]: { label: string; tone: PillTone } } = {
  DRAFT: { label: "Not validated", tone: "neutral" },
  READY: { label: "Ready", tone: "success" },
  NOT_READY: { label: "Not ready", tone: "danger" },
  ARCHIVED: { label: "Archived", tone: "neutral" },
};
export const CHECK_TONES: { [status in DrCheckStatus]: PillTone } = {
  PASS: "success",
  WARNING: "warning",
  FAIL: "danger",
  NOT_CONFIGURED: "neutral",
};
const CHECK_LABELS: { [status in DrCheckStatus]: string } = {
  PASS: "Pass",
  WARNING: "Warning",
  FAIL: "Fail",
  NOT_CONFIGURED: "Not configured",
};
const AREA_LABELS: { [area: string]: string } = {
  coverage: "Recovery order",
  protection: "Protection",
  recovery_points: "Recovery points",
  capability: "DR site can recover it",
  network: "Networks",
  ip: "IP addresses",
  dns: "DNS",
  security: "Security",
  health_checks: "Health checks",
  test_isolation: "Test isolation",
  capacity: "Capacity",
};
const AREA_ORDER = Object.keys(AREA_LABELS);

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  & + & {
    margin-top: 20px;
  }
`;
const Rows = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;
const Row = styled.li`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 4px 12px;
  padding: 7px 0;
  border-bottom: 1px solid ${T9.slate100};
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate700};
  word-break: break-word;
`;
const WaveName = styled.div`
  font-weight: 600;
  color: ${T9.slate900};
`;
const Mono = styled(OpsTextarea)`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  min-height: 220px;
`;
const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** One wave per group, each with all of its VMs: a starting point the person refines. */
export const specFromGroups = (groups: DrGroup[], workloads: DrWorkload[], groupIds: string[]): DrPlanSpec => ({
  waves: groupIds.map(id => ({
    name: groups.find(g => g.id === id)?.name || id,
    workloads: workloads.filter(w => w.group_id === id).map(w => w.id),
  })),
});

// ------------------------------------------------------------------ create / edit

type PlanDialogProps = {
  plan?: DrPlan | null;
  groups: DrGroup[];
  workloads: DrWorkload[];
  onClose: () => void;
  onSaved: (plan: DrPlan) => void;
};
export const PlanDialog = (props: PlanDialogProps) => {
  const p = props.plan;
  const editing = Boolean(p);
  const [name, setName] = React.useState(p?.name || "");
  const [description, setDescription] = React.useState(p?.description || "");
  const [groupIds, setGroupIds] = React.useState<string[]>(p?.group_ids || []);
  const [specText, setSpecText] = React.useState(p?.spec ? JSON.stringify(p.spec, null, 2) : "");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const generate = (ids: string[]) => setSpecText(JSON.stringify(specFromGroups(props.groups, props.workloads, ids), null, 2));
  const toggle = (id: string, on: boolean) => {
    const next = on ? [...groupIds, id] : groupIds.filter(g => g !== id);
    setGroupIds(next);
    if (!editing && !specText.trim()) generate(next);
  };

  const submit = async () => {
    let spec: DrPlanSpec;
    try {
      spec = specText.trim() ? JSON.parse(specText) : specFromGroups(props.groups, props.workloads, groupIds);
    } catch (err: any) {
      setError(`The plan spec is not valid JSON: ${err.message}`);
      return;
    }
    if (!name.trim()) return setError("Give the plan a name.");
    if (!groupIds.length) return setError("Choose the protection groups the plan recovers.");
    setBusy(true);
    setError(null);
    try {
      const saved = editing
        ? await drSource.updatePlan(p!.id, {
            name: name.trim(),
            description: description.trim() || null,
            group_ids: groupIds,
            spec,
            reason: reason.trim() || undefined,
          })
        : await drSource.createPlan({ name: name.trim(), description: description.trim() || null, group_ids: groupIds, spec });
      props.onSaved(saved);
    } catch (err) {
      setError(drErrorMessage(err, "Could not save the plan."));
      setBusy(false);
    }
  };

  return (
    <OpsDialog
      title={editing ? `Edit plan ${p!.name}` : "New DR plan"}
      subtitle="Which VMs a recovery brings up, in which order, and how they are reached at the DR site."
      icon={ListChecks}
      width={720}
      onRequestClose={props.onClose}
      testId="dr-plan-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={editing ? "Save as a new version" : "Create plan"}
          busyLabel="Saving…"
          note="Saving makes a new version; validate it before a recovery uses it."
          testId="dr-plan-submit"
        />
      }
    >
      <ErrorNotice error={error} />
      <FormGrid columns={2}>
        <FormField label="Name" required span={2}>
          <OpsInput value={name} onChange={e => setName(e.target.value)} placeholder="ERP to AWS" data-test-id="dr-plan-name" />
        </FormField>
        <FormField label="Protection groups" required span={2} as="div">
          <GroupList>
            {props.groups.map(g => (
              <CheckRow
                key={g.id}
                checked={groupIds.includes(g.id)}
                onChange={on => toggle(g.id, on)}
                label={g.name}
                description={`${props.workloads.filter(w => w.group_id === g.id).length} VM(s)`}
              />
            ))}
            {!props.groups.length ? <Muted>Create a protection group first.</Muted> : null}
          </GroupList>
        </FormField>
        <FormField label="Plan spec (JSON)" span={2} as="div">
          <Mono value={specText} onChange={e => setSpecText(e.target.value)} spellCheck={false} data-test-id="dr-plan-spec" />
          <FieldHint>
            Waves recover in order; each lists workload IDs. Optional: network, ip, dns (route53, azure, rfc2136), security,
            health_checks.{" "}
            <a
              href="#generate"
              onClick={e => {
                e.preventDefault();
                generate(groupIds);
              }}
            >
              One wave per group
            </a>
          </FieldHint>
        </FormField>
        <FormField label="Description" span={2}>
          <OpsInput value={description} onChange={e => setDescription(e.target.value)} />
        </FormField>
        {editing ? (
          <FormField label="Why this version" span={2}>
            <OpsInput value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. health check for the database" />
          </FormField>
        ) : null}
      </FormGrid>
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ point-in-time recovery

type PointChoice = { [workloadId: string]: string };

/** Which recovery point each workload of a failover comes back from: its newest by default, or an older restore
 * point where its DR site keeps several (e.g. before a corruption or ransomware reached the replica). */
export const RecoveryPointPicker = (props: {
  workloadIds: string[];
  name: (id: string) => string;
  value: PointChoice;
  onChange: (value: PointChoice) => void;
}) => {
  const [points, setPoints] = React.useState<{ [id: string]: DrRecoveryPoint[] }>({});
  const key = props.workloadIds.join(",");
  React.useEffect(() => {
    let live = true;
    props.workloadIds.forEach(async id => {
      let list: DrRecoveryPoint[] = [];
      try {
        list = (await drSource.recoveryPoints(id)).filter(p => p.recoverable);
      } catch {
        list = [];
      }
      if (live) setPoints(current => ({ ...current, [id]: list }));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!props.workloadIds.length) return null;
  const choose = (id: string, pointId: string) => {
    const next = { ...props.value };
    if (pointId) next[id] = pointId;
    else delete next[id];
    props.onChange(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-test-id="dr-plan-recovery-points">
      <strong style={{ fontSize: 13 }}>Recover from</strong>
      {props.workloadIds.map(id => {
        const list = points[id];
        const newest = (list || []).find(p => p.latest);
        const older = (list || []).filter(p => p.kind === "restore-point" && !p.latest);
        const newestLabel = newest ? `Newest point (${formatDateTime(newest.taken_at)})` : "Newest point";
        return (
          <label key={id} style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ minWidth: 140 }}>{props.name(id)}</span>
            {list === undefined ? (
              <Muted>Loading recovery points…</Muted>
            ) : older.length === 0 ? (
              <Muted>
                {newestLabel}
                {newest?.kind === "latest-replica" ? "; its DR site keeps only this one" : ""}
              </Muted>
            ) : (
              <select
                value={props.value[id] || ""}
                onChange={e => choose(id, e.target.value)}
                aria-label={`Recovery point of ${props.name(id)}`}
                data-test-id={`dr-plan-point-${id}`}
              >
                <option value="">{newestLabel}</option>
                {older.map(p => (
                  <option key={p.id} value={p.id}>
                    {formatDateTime(p.taken_at)}
                    {p.consistency ? ` · ${p.consistency}-consistent` : ""}
                  </option>
                ))}
              </select>
            )}
          </label>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------------ drawer

type PlanDrawerProps = {
  plan: DrPlan;
  groups: DrGroup[];
  workloads: DrWorkload[];
  onClose: () => void;
  onEdit: (plan: DrPlan) => void;
  onArchive: () => void;
  onChanged: () => void;
};
export const PlanDrawer = (props: PlanDrawerProps) => {
  const [plan, setPlan] = React.useState<DrPlan>(props.plan);
  const [run, setRun] = React.useState<DrRun | null>(null);
  const [versions, setVersions] = React.useState<DrPlanVersion[] | null>(null);
  const [runs, setRuns] = React.useState<DrRun[]>([]);
  const [openRun, setOpenRun] = React.useState<string | null>(null);
  const [failoverMode, setFailoverMode] = React.useState<"planned" | "emergency" | "test" | "failback" | null>(null);
  const [failoverReason, setFailoverReason] = React.useState("");
  const [pointChoice, setPointChoice] = React.useState<PointChoice>({});
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const name = (id: string) => props.workloads.find(w => w.id === id)?.name || id;

  const load = React.useCallback(async () => {
    try {
      const full = await drSource.plan(props.plan.id);
      setPlan(full);
      setRun(full.last_validation ? await drSource.run(full.last_validation.run_id) : null);
      setVersions(await drSource.planVersions(props.plan.id));
      setRuns(await drSource.runs(props.plan.id));
    } catch (err) {
      setError(drErrorMessage(err, "Could not load the plan."));
    }
  }, [props.plan.id]);
  React.useEffect(() => {
    load();
  }, [load, props.plan.current_version, props.plan.state]);

  const act = async (label: string, fn: () => Promise<any>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await load();
      props.onChanged();
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ refused this."));
    } finally {
      setBusy(null);
    }
  };

  const state = PLAN_STATES[plan.state] || { label: plan.state, tone: "neutral" as PillTone };
  const archived = plan.state === "ARCHIVED";
  const checks = [...(run?.result?.checks || [])].sort(
    (a, b) => AREA_ORDER.indexOf(a.area) - AREA_ORDER.indexOf(b.area),
  );
  const stale = run && run.plan_version !== plan.current_version;
  const inherited = Object.entries(plan.spec_sources || {})
    .filter(([, source]) => source === "client" || source === "plan+client")
    .map(([section]) => section);
  return (
    <OpsDrawer
      title={plan.name}
      subtitle={`Version ${plan.current_version} · ${plan.group_ids.map(id => props.groups.find(g => g.id === id)?.name || id).join(", ")}`}
      icon={ListChecks}
      tone={plan.state === "READY" ? "success" : plan.state === "NOT_READY" ? "danger" : "neutral"}
      width={680}
      onClose={props.onClose}
      testId="dr-plan-drawer"
      footer={
        <>
          {!archived ? (
            <ActionButton type="button" onClick={() => act("validate", () => drSource.validatePlan(plan.id))} disabled={Boolean(busy)} data-test-id="dr-plan-validate">
              <ClipboardCheck size={16} /> {busy === "validate" ? "Validating…" : "Validate"}
            </ActionButton>
          ) : null}
          {!archived ? (
            <ActionButton type="button" variant="secondary" onClick={() => props.onEdit(plan)}>
              <Pencil size={16} /> Edit…
            </ActionButton>
          ) : null}
          {!archived ? (
            <ActionButton
              type="button"
              variant="secondary"
              onClick={() => setFailoverMode(failoverMode === "planned" ? null : "planned")}
              disabled={Boolean(busy)}
              data-test-id="dr-plan-failover"
            >
              <PlaneTakeoff size={16} /> Fail over…
            </ActionButton>
          ) : null}
          {!archived ? (
            <ActionButton
              type="button"
              variant="secondary"
              onClick={() => setFailoverMode(failoverMode === "test" ? null : "test")}
              disabled={Boolean(busy)}
              data-test-id="dr-plan-test"
            >
              <FlaskConical size={16} /> Test failover…
            </ActionButton>
          ) : null}
          {!archived ? (
            <ActionButton
              type="button"
              variant="danger"
              onClick={() => setFailoverMode(failoverMode === "emergency" ? null : "emergency")}
              disabled={Boolean(busy)}
              data-test-id="dr-plan-emergency"
            >
              <Siren size={16} /> Emergency failover…
            </ActionButton>
          ) : null}
          {!archived ? (
            <ActionButton
              type="button"
              variant="secondary"
              onClick={() => setFailoverMode(failoverMode === "failback" ? null : "failback")}
              disabled={Boolean(busy)}
              data-test-id="dr-plan-failback"
            >
              <PlaneLanding size={16} /> Fail back…
            </ActionButton>
          ) : null}
          <FootSpacer />
          {!archived ? (
            <ActionButton type="button" variant="danger" onClick={props.onArchive}>
              <Archive size={16} /> Archive…
            </ActionButton>
          ) : null}
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {failoverMode ? (
        <Notice
          tone={failoverMode === "emergency" ? "danger" : "warning"}
          title={
            failoverMode === "emergency"
              ? "Emergency failover"
              : failoverMode === "test"
                ? "Test failover"
                : failoverMode === "failback"
                  ? "Failback"
                  : "Planned failover"
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-test-id="dr-plan-failover-form">
            {failoverMode === "emergency" ? (
              <span>
                For when the production site is down. The failover starts at once, without an approval (your role must
                allow emergency failovers), and someone else reviews it afterwards. There is no final sync: the VMs come
                back from their newest recovery point, so the data written after it is lost. SwiftIQ powers the production VMs
                off if their site still answers; where it cannot, your reason is the acknowledgment that they may still be
                running.
              </span>
            ) : failoverMode === "test" ? (
              <span>
                After someone else approves it in Change control, every VM of the plan starts from its newest recovery
                point on the plan's isolated test networks, wave by wave, and is probed. Production, the replication and
                the DR site's networks are not touched. Clean the test VMs up from the run when you are done.
              </span>
            ) : failoverMode === "failback" ? (
              <span>
                After someone else approves it in Change control, each VM running at the DR site is copied back into its
                original while it keeps running. Then every DR VM is shut down for a final sync, the originals start wave
                by wave, and protection resumes from that point. The DR copies stay until you confirm the failback from
                the run.
              </span>
            ) : (
              <span>
                SwiftIQ validates the plan again, then asks someone else to approve the failover in Change control. Once
                approved, every production VM of the plan is shut down for a final sync and the VMs start at the DR site,
                wave by wave.
                {Object.values(pointChoice).some(Boolean) ? (
                  <strong data-test-id="dr-plan-failover-point-warning">
                    {" "}
                    The VMs with a chosen restore point start from that point instead of the final sync: what they wrote
                    after it is not recovered (the final sync is still kept at the DR site).
                  </strong>
                ) : null}
              </span>
            )}
            <OpsInput
              value={failoverReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFailoverReason(e.target.value)}
              placeholder={
                failoverMode === "emergency"
                  ? "Reason and acknowledgment (required, kept in the audit log)"
                  : "Reason (shown to the approver and kept in the audit log)"
              }
              data-test-id="dr-plan-failover-reason"
            />
            {failoverMode !== "failback" ? (
              <RecoveryPointPicker
                workloadIds={(plan.spec?.waves || []).flatMap(w => w.workloads || [])}
                name={name}
                value={pointChoice}
                onChange={setPointChoice}
              />
            ) : null}
            <div style={{ display: "flex", gap: 8 }}>
              <ActionButton
                type="button"
                variant={failoverMode === "emergency" ? "danger" : undefined}
                disabled={Boolean(busy) || (failoverMode === "emergency" && !failoverReason.trim())}
                data-test-id="dr-plan-failover-request"
                onClick={() =>
                  act("failover", async () => {
                    const run = await drSource.failover(
                      plan.id,
                      failoverReason.trim() || undefined,
                      failoverMode,
                      failoverMode === "failback" ? undefined : pointChoice,
                    );
                    setFailoverMode(null);
                    setFailoverReason("");
                    setPointChoice({});
                    setOpenRun(run.id);
                  })
                }
              >
                {failoverMode === "emergency" ? (
                  <Siren size={16} />
                ) : failoverMode === "failback" ? (
                  <PlaneLanding size={16} />
                ) : (
                  <PlaneTakeoff size={16} />
                )}{" "}
                {busy === "failover"
                  ? failoverMode === "emergency"
                    ? "Starting…"
                    : "Requesting…"
                  : failoverMode === "emergency"
                    ? "Start emergency failover"
                    : failoverMode === "test"
                      ? "Request test failover"
                      : failoverMode === "failback"
                        ? "Request failback"
                        : "Request failover"}
              </ActionButton>
              <ActionButton type="button" variant="secondary" onClick={() => setFailoverMode(null)}>
                Cancel
              </ActionButton>
            </div>
          </div>
        </Notice>
      ) : null}
      <Section>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Pill tone={state.tone}>{state.label}</Pill>
          {run?.result ? (
            <Pill tone={CHECK_TONES[run.result.overall]}>
              Validation: {CHECK_LABELS[run.result.overall]} ({run.result.counts.FAIL || 0} failed, {run.result.counts.WARNING || 0} warnings)
            </Pill>
          ) : null}
        </div>
        {plan.state === "DRAFT" ? (
          <Notice tone="neutral" title="Not validated">
            Version {plan.current_version} has not been validated. Validate it to see whether a recovery can run now.
          </Notice>
        ) : null}
        {plan.description ? <Muted>{plan.description}</Muted> : null}
        {inherited.length ? (
          <Muted data-test-id="dr-plan-inherited">
            From the client's DR settings: {inherited.map(s => AREA_LABELS[s] || s).join(", ")}.
          </Muted>
        ) : null}
      </Section>

      <Section>
        <SectionHeading>Recovery order</SectionHeading>
        <Rows>
          {(plan.spec?.waves || []).map((wave, i) => (
            <Row key={i}>
              <WaveName>{wave.name || `Wave ${i + 1}`}</WaveName>
              <span>{wave.workloads.length ? wave.workloads.map(name).join(", ") : <Muted>Nothing</Muted>}</span>
            </Row>
          ))}
        </Rows>
      </Section>

      <Section data-test-id="dr-plan-checks">
        <SectionHeading>Validation</SectionHeading>
        {run ? (
          <Muted>
            {stale ? `Of version ${run.plan_version}. ` : ""}
            <span title={formatDateTime(run.finished_at || run.created_at)}>{timeAgo(run.finished_at || run.created_at)}</span>
            {run.requested_by ? ` by ${run.requested_by}` : ""}
          </Muted>
        ) : (
          <Muted>Not validated yet.</Muted>
        )}
        <Rows>
          {checks.map((c: DrPlanCheck, i) => (
            <Row key={i}>
              <span>
                <Pill tone={CHECK_TONES[c.status]}>{CHECK_LABELS[c.status]}</Pill>
              </span>
              <span>
                <strong>{AREA_LABELS[c.area] || c.area}:</strong> {c.message}
              </span>
            </Row>
          ))}
        </Rows>
      </Section>

      <Section data-test-id="dr-plan-runs">
        <SectionHeading>Runs</SectionHeading>
        {runs.length ? (
          <Rows>
            {runs.map(r => {
              const rs = RUN_STATES[r.state] || { label: r.state, tone: "neutral" as PillTone };
              return (
                <React.Fragment key={r.id}>
                  <Row>
                    <span>
                      <Pill tone={rs.tone}>{rs.label}</Pill>
                    </span>
                    <span>
                      <a
                        href="#run"
                        data-test-id={`dr-plan-run-${r.id}`}
                        onClick={e => {
                          e.preventDefault();
                          setOpenRun(openRun === r.id ? null : r.id);
                        }}
                      >
                        {OPERATION_LABELS[r.operation] || r.operation}
                      </a>
                      {` · version ${r.plan_version ?? "?"} · `}
                      <span title={formatDateTime(r.created_at)}>{timeAgo(r.created_at)}</span>
                      {r.requested_by ? ` · ${r.requested_by}` : ""}
                    </span>
                  </Row>
                  {openRun === r.id ? <RunView runId={r.id} onChanged={load} /> : null}
                </React.Fragment>
              );
            })}
          </Rows>
        ) : (
          <Muted>No runs yet.</Muted>
        )}
      </Section>

      <Section>
        <SectionHeading>
          <History size={14} /> Versions
        </SectionHeading>
        <Rows>
          {(versions || []).map(v => (
            <Row key={v.version}>
              <span>
                Version {v.version} {v.current ? <Pill tone="brand">Current</Pill> : null}
              </span>
              <span>
                {v.reason || "—"} · {v.created_by || "?"} · <span title={formatDateTime(v.created_at)}>{timeAgo(v.created_at)}</span>
                {!v.current && !archived ? (
                  <>
                    {" · "}
                    <a
                      href="#restore"
                      onClick={e => {
                        e.preventDefault();
                        act("restore", () => drSource.updatePlan(plan.id, { restore_version: v.version }));
                      }}
                    >
                      <RotateCcw size={12} /> Restore
                    </a>
                  </>
                ) : null}
              </span>
            </Row>
          ))}
        </Rows>
      </Section>
    </OpsDrawer>
  );
};
